import { useEffect, useMemo } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";

import NotFoundPage from "./pages/pages/404";
import MaintenancePage from "./pages/pages/maintenance";

import { ActionDialogRoots } from "@/action-dialog";
import AuthenticationGuard from "./components/authentication-guard";
import LoginPage from "./pages/auth/login";
import LogoutPage from "./pages/auth/logout";
import SSOSetupTutorial from "./pages/pages/sso_setup";

import { useFlags } from "launchdarkly-react-client-sdk";
import { PENDING_BETA_INVITE_CODE_KEY } from "./constants/betaInvite";
import { ROUTES } from "./constants/routes";
import { hasFreshTemplateSignupIntent } from "./constants/templateSignup";
import { OnboardingProvider } from "./context/OnboardingContext";
import { OpenTabsProvider } from "./context/OpenTabsContext";
import { TabPreloadProvider } from "./context/TabPreloadContext";
import { Permissions } from "./hooks/usePermissions";
import { useTrackPageView } from "./hooks/useTrackPageView";
import { useTrackUser } from "./hooks/useTrackUser";
import { OnboardingLayout } from "./layouts/OnboardingLayout";
import { SettingsRouteSync } from "./layouts/shared/SettingsRouteSync";
import WorkspaceLayout from "./layouts/WorkspaceLayout";
import {
  Flags,
  flagValues,
  LaunchDarklyProvider,
} from "./providers/LaunchDarklyProvider";

import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
  AuthorizationParams,
} from "@auth0/auth0-react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PropsWithChildren, StrictMode } from "react";

import { config } from "./config";
import "./index.css";

import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import React from "react";
import { BrowserRouter } from "react-router-dom";
import * as analytics from "./utils/analytics";

import { ElementsProvider, ThemeProvider } from "@cohesive/ui-components";
import { Toaster } from "react-hot-toast";
import { AuthInject, orgAPI } from "./api/api";
import { auth0Config } from "./auth0.config";
import { LiveblocksAuthProvider } from "./context/LiveblocksAuthProvider";
import { OnScreenEditorsProvider } from "./context/OnScreenEditorsContext";
import { PaneLayoutWidthsProvider } from "./context/PaneLayoutWidthsContext";
import { nativeLoginOpts } from "./native/auth";
import { NativeAppUrlListener } from "./native/NativeAppUrlListener";
import { NativeQuickActionListener } from "./native/NativeQuickActionListener";
import { isNativePlatform, isRealNativePlatform } from "./native/platform";
import { ShareAuthIdentitySync } from "./native/ShareAuthIdentitySync";
import { HomeGate } from "./pages/mobile/HomeGate";
import { MobileHomeSurface } from "./pages/mobile/MobileHomeSurface";
import { MobileVoiceGate } from "./pages/mobile/MobileVoiceGate";
import { WebOnlyPage } from "./pages/mobile/WebOnlyPage";
import { WorkspaceGate } from "./pages/mobile/WorkspaceGate";
import { queryClient } from "./queryClient";
import { persistOptions, QueryCacheIdentityGuard } from "./queryPersistence";

import { useAuth0 } from "@auth0/auth0-react";
import { useThemeContext } from "@cohesive/ui-components";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import colorConvert from "color-convert";
import { motion } from "motion/react";
import { titleCase } from "title-case";
import { useSetupIntentClientSecret } from "./api/stripe";
import { useUserInfo } from "./api/user";
import MySkills from "./components/AdminPanel/Account/MySkills";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { useAppBadge } from "./hooks/useAppBadge";
import { useIsSelfServeBilling } from "./hooks/useIsSelfServeBilling";
import { useRequireSubscription } from "./hooks/useRequireSubscription";
import { useSsoDomainLoginRecovery } from "./hooks/useSsoDomainLoginRecovery";
import "./lib/liveblocks/liveblocks.config";
import ErrorPage from "./pages/auth/error/ErrorPage";
import { useLoginRedirect } from "./pages/auth/login/LoginPage";
import SignUpPage from "./pages/auth/signup/SignUpPage";
import { CheckoutPage } from "./pages/billing/CheckoutPage";
import { ConfirmSetupPage } from "./pages/billing/ConfirmSetupPage";
import { EnterpriseInquiryPage } from "./pages/billing/EnterpriseInquiryPage";
import { PlansPage } from "./pages/billing/PlansPage";
import { CanvasPage } from "./pages/canvas/CanvasPage";
import { FileRecordPage } from "./pages/file-record/FileRecordPage";
import { BetaInviteContinuePage } from "./pages/invite/BetaInviteContinuePage";
import { BetaInvitePage } from "./pages/invite/BetaInvitePage";
import { CreateOrganizationPage } from "./pages/onboarding/CreateOrganizationPage";
import { OnboardingEnterpriseInquiryPage } from "./pages/onboarding/EnterpriseInquiryPage";
import { SelectPlanPage } from "./pages/onboarding/SelectPlanPage";
import { TemplateSetupPage } from "./pages/onboarding/TemplateSetupPage";
import { WorkspaceChatPage } from "./pages/workspaces/chat/WorkspaceChatPage";
import { HomePage } from "./pages/workspaces/home/HomePage";
import { SharedWithMePage } from "./pages/workspaces/SharedWithMePage";
import { WorkspaceDetailPage } from "./pages/workspaces/WorkspaceDetailPage";
import { EventStreamProvider } from "./providers/EventStreamProvider";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function hslToRgb(hsl: string) {
  const ha = hsl.split(" ").map(parseFloat);
  const rgb = colorConvert.hsl.rgb(ha[0], ha[1], ha[2]);
  const str = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;

  return str;
}

function TrackUserProvider({ children }: { children: React.ReactNode }) {
  useTrackPageView();
  useTrackUser();

  return children;
}

function PreloadDataProvider({ children }: { children: React.ReactNode }) {
  useAppBadge();

  return children;
}

function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  useRequireSubscription();
  return children;
}

const Auth0ProviderWithRedirectCallback = ({
  children,
  ...props
}: PropsWithChildren<Auth0ProviderOptions>) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate((appState && appState.returnTo) || window.location.pathname);
    analytics.trackLogin();
  };

  return (
    <Auth0Provider onRedirectCallback={onRedirectCallback} {...props}>
      {children}
    </Auth0Provider>
  );
};

interface AuthConfig {
  authorizationParams?: AuthorizationParams;
  appState?: AppState;
}

export function getAuthConfig(): AuthConfig {
  const searchParams = new URLSearchParams(window.location.search);

  const orgId = searchParams.get("org");

  if (orgId) {
    // Construct returnTo URL without the org parameter
    const cleanedParams = new URLSearchParams(searchParams);
    cleanedParams.delete("org");

    const pathname = window.location.pathname;
    const search = cleanedParams.toString();
    const returnTo = search ? `${pathname}?${search}` : pathname;

    return {
      authorizationParams: {
        organization: orgId,
      },
      appState: {
        returnTo: returnTo,
      },
    };
  }

  return {};
}

function OrganizationDeepLinkWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const searchParams = new URLSearchParams(window.location.search);
  const orgId = searchParams.get("org");
  const { loginWithRedirect } = useAuth0();

  if (orgId) {
    const authConfig = getAuthConfig();

    loginWithRedirect({ ...authConfig, ...nativeLoginOpts() });

    return null;
  }

  return children;
}

function AppWithAuthBoundary() {
  const appTree = (
    <>
      <AuthInject />
      <NativeAppUrlListener />
      <NativeQuickActionListener />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => {
          void queryClient.resumePausedMutations();
        }}
      >
        <QueryCacheIdentityGuard>
          <ShareAuthIdentitySync />
          <ActionDialogRoots />
          <App />

          <Toaster
            position="bottom-right"
            containerStyle={{
              zIndex: 999999,
            }}
            toastOptions={{
              duration: 4000,
              className: "text-sm font-medium",
            }}
          />

          {config.devtools.reactQuery && !navigator.webdriver && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryCacheIdentityGuard>
      </PersistQueryClientProvider>
    </>
  );

  return (
    <Auth0ProviderWithRedirectCallback {...auth0Config}>
      <LiveblocksAuthProvider>
        <OrganizationDeepLinkWrapper>{appTree}</OrganizationDeepLinkWrapper>
      </LiveblocksAuthProvider>
    </Auth0ProviderWithRedirectCallback>
  );
}

const AppWithProviders = () => {
  return (
    <StrictMode>
      <ThemeProvider>
        <OnScreenEditorsProvider>
          <BrowserRouter>
            <PaneLayoutWidthsProvider>
              <AppWithAuthBoundary />
            </PaneLayoutWidthsProvider>
          </BrowserRouter>
        </OnScreenEditorsProvider>
      </ThemeProvider>
    </StrictMode>
  );
};

const ElementsWithTheme = ({ children }: { children: React.ReactNode }) => {
  const { mode } = useThemeContext();
  const isCheckoutPage = useMatch(ROUTES.BILLING.CHECKOUT);
  const {
    data: setupIntentClientSecret,
    isLoading: isSetupIntentClientSecretLoading,
  } = useSetupIntentClientSecret({
    enabled: !!isCheckoutPage,
  });

  const appearance: StripeElementsOptions["appearance"] = useMemo(() => {
    const styles = getComputedStyle(document.body);

    return {
      variables: {
        colorPrimary:
          hslToRgb(styles.getPropertyValue("--foreground")) || undefined,
        colorBackground:
          hslToRgb(styles.getPropertyValue("--background")) || undefined,
        colorText: styles.getPropertyValue("--n-90") || undefined,
        colorTextSecondary: styles.getPropertyValue("--n-50") || undefined,
        borderRadius: styles.getPropertyValue("--radius") || undefined,
        fontSizeBase: styles.getPropertyValue("--text-base") || undefined,
        fontSizeSm: styles.getPropertyValue("--text-sm") || undefined,
        fontSizeXs: styles.getPropertyValue("--text-xs") || undefined,
        fontSize2Xs: styles.getPropertyValue("--text-2xs") || undefined,
        fontSize3Xs: styles.getPropertyValue("--text-3xs") || undefined,
        fontSizeLg: styles.getPropertyValue("--text-lg") || undefined,
        fontSizeXl: styles.getPropertyValue("--text-xl") || undefined,
        fontWeightLight:
          styles.getPropertyValue("--font-weight-light") || undefined,
        fontWeightNormal:
          styles.getPropertyValue("--font-weight-normal") || undefined,
        fontWeightMedium:
          styles.getPropertyValue("--font-weight-medium") || undefined,
        fontWeightBold:
          styles.getPropertyValue("--font-weight-bold") || undefined,
        spacingUnit: styles.getPropertyValue("--spacing-unit") || undefined,
        gridRowSpacing:
          styles.getPropertyValue("--grid-row-spacing") || undefined,
        gridColumnSpacing:
          styles.getPropertyValue("--grid-column-spacing") || undefined,
        tabSpacing: styles.getPropertyValue("--tab-spacing") || undefined,
        accordionItemSpacing:
          styles.getPropertyValue("--accordion-item-spacing") || undefined,
      },
    };
  }, [mode]);

  if (isCheckoutPage) {
    if (isSetupIntentClientSecretLoading) {
      return null;
    }

    const options: StripeElementsOptions = {
      clientSecret: setupIntentClientSecret?.data.client_secret,
      currency: "usd",
      appearance: appearance,
    };

    return (
      <Elements stripe={stripePromise} options={options}>
        {children}
      </Elements>
    );
  }

  const options: StripeElementsOptions = {
    mode: "setup",
    currency: "usd",
    // Must match the SetupIntent's payment_method_types from
    // orgCreateStripeSetupIntentHandler. Self-serve onboarding is pro-only,
    // which the backend gates to "card". Mismatching here causes Stripe to
    // reject confirmSetup with an "automatic payment methods" error.
    paymentMethodTypes: ["card"],
    appearance: appearance,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};

function ConditionalAppLayout() {
  const flags = useFlags<Flags>();
  const isMaintenanceMode = flags.maintenanceMode ?? false;

  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <WorkspaceLayout>
      <ErrorBoundary>
        <Routes>
          <Route index element={<HomeGate fullApp={<HomePage />} />} />

          {/* Native mobile surfaces overlay the canvas-centric shell */}
          <Route path={ROUTES.MOBILE.HOME} element={<MobileHomeSurface />} />
          <Route path={ROUTES.MOBILE.VOICE} element={<MobileVoiceGate />} />

          <Route
            path={ROUTES.WORKSPACES.DETAIL}
            element={<WorkspaceGate fullApp={<WorkspaceDetailPage />} />}
          />
          <Route path={ROUTES.SHARED.ROOT} element={<SharedWithMePage />} />
          <Route
            path={ROUTES.ARCHIVE.ROOT}
            element={<Navigate to={ROUTES.SETTINGS.ARCHIVE} replace />}
          />
          <Route
            path={ROUTES.ARCHIVE.LEGACY_TRASH}
            element={<Navigate to={ROUTES.SETTINGS.ARCHIVE} replace />}
          />
          <Route path={ROUTES.CANVAS.DETAIL} element={<CanvasPage />} />
          <Route
            path={ROUTES.CANVAS.ROOT}
            element={<Navigate to="/" replace />}
          />
          <Route
            path={ROUTES.FILE_RECORDS.DETAIL}
            element={<FileRecordPage />}
          />

          <Route path={ROUTES.SKILLS.ROOT} element={<MySkills />} />

          <Route path={ROUTES.CHAT.DETAIL} element={<WorkspaceChatPage />} />

          <Route path="settings/*" element={<SettingsRouteSync />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </WorkspaceLayout>
  );
}

function App() {
  useLoginRedirect();
  const recoveringSsoLogin = useSsoDomainLoginRecovery();

  const { error, loginWithRedirect } = useAuth0();
  const { data: userInfo, isLoading } = useUserInfo();
  const isSelfServeBilling = useIsSelfServeBilling();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkOrganization = async () => {
      if (
        Array.isArray(userInfo?.organizations) &&
        userInfo?.organizations?.length > 0 &&
        userInfo?.organization === null
      ) {
        await loginWithRedirect({
          authorizationParams: {
            prompt: "select_account",
          },
          ...nativeLoginOpts(),
        });
      }

      const hasPendingBetaInvite =
        typeof sessionStorage !== "undefined" &&
        !!sessionStorage.getItem(PENDING_BETA_INVITE_CODE_KEY);
      const hasPendingTemplateIntent =
        typeof sessionStorage !== "undefined" && hasFreshTemplateSignupIntent();
      const onPublicInvitePath =
        location.pathname === ROUTES.INVITE ||
        location.pathname === ROUTES.INVITE_CONTINUE;

      if (
        userInfo?.organizations?.length === 0 &&
        !onPublicInvitePath &&
        !hasPendingBetaInvite &&
        location.pathname !== ROUTES.ONBOARDING.TEMPLATE_SETUP
      ) {
        navigate(
          hasPendingTemplateIntent
            ? ROUTES.ONBOARDING.TEMPLATE_SETUP
            : ROUTES.ONBOARDING.CREATE_ORGANIZATION,
        );
      }
    };

    checkOrganization();
  }, [userInfo, location.pathname, navigate, loginWithRedirect]);

  if (recoveringSsoLogin) {
    return null;
  }

  if (error) {
    return (
      <ErrorPage
        title="Not Authorized"
        message={titleCase(error?.message || "", {
          sentenceCase: true,
        })}
      />
    );
  }

  if (
    Array.isArray(userInfo?.organizations) &&
    userInfo?.organizations?.length > 0 &&
    userInfo?.organization === null
  ) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Routes>
        {/* Pages Routes */}
        <Route path={ROUTES.PAGES.MAINTENANCE} element={<MaintenancePage />} />
        <Route path={ROUTES.PAGES.SSO_SETUP} element={<SSOSetupTutorial />} />

        {/* Auth Routes */}
        <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
        <Route
          path={ROUTES.AUTH.SIGNUP}
          element={
            isNativePlatform() ? (
              <Navigate to={ROUTES.AUTH.LOGIN} replace />
            ) : (
              <SignUpPage />
            )
          }
        />
        <Route path={ROUTES.AUTH.LOGOUT} element={<LogoutPage />} />
        <Route
          path={ROUTES.AUTH.ERROR}
          element={
            <ErrorPage
              title="Not Authorized"
              message="You are not authorized to access this page"
            />
          }
        />

        <Route
          path={ROUTES.INVITE}
          element={
            <ErrorBoundary>
              <BetaInvitePage />
            </ErrorBoundary>
          }
        />
        <Route
          path={ROUTES.INVITE_CONTINUE}
          element={
            <ErrorBoundary>
              <BetaInviteContinuePage />
            </ErrorBoundary>
          }
        />

        <Route
          path={ROUTES.ONBOARDING.ROOT}
          element={
            <LaunchDarklyProvider>
              <AuthenticationGuard>
                <TrackUserProvider>
                  <OnScreenEditorsProvider>
                    <ErrorBoundary>
                      <Outlet />
                    </ErrorBoundary>
                  </OnScreenEditorsProvider>
                </TrackUserProvider>
              </AuthenticationGuard>
            </LaunchDarklyProvider>
          }
        >
          <Route
            element={
              <OnboardingProvider>
                <OnboardingLayout />
              </OnboardingProvider>
            }
          >
            <Route
              path={ROUTES.ONBOARDING.CREATE_ORGANIZATION}
              element={<CreateOrganizationPage />}
            />
            <Route
              path={ROUTES.ONBOARDING.TEMPLATE_SETUP}
              element={<TemplateSetupPage />}
            />
          </Route>

          {/* Plan Selection and Checkout Routes - requires Stripe Elements.
              Hidden only in the real iOS container (Apple 3.1.1): no
              plan/checkout/payment UI in the app; browsers always get it. */}
          <Route
            element={
              isRealNativePlatform() ? (
                <WebOnlyPage />
              ) : (
                <OnboardingProvider>
                  <ElementsProvider apiClient={orgAPI}>
                    <ElementsWithTheme>
                      <OnboardingLayout />
                    </ElementsWithTheme>
                  </ElementsProvider>
                </OnboardingProvider>
              )
            }
          >
            <Route
              path={ROUTES.ONBOARDING.SELECT_PLAN}
              element={<SelectPlanPage />}
            />
            <Route
              path={ROUTES.ONBOARDING.ENTERPRISE_INQUIRY}
              element={<OnboardingEnterpriseInquiryPage />}
            />
            <Route
              path={ROUTES.ONBOARDING.CHECKOUT}
              element={<CheckoutPage />}
            />
            <Route
              path={ROUTES.ONBOARDING.CONFIRM_SETUP}
              element={<ConfirmSetupPage />}
            />
          </Route>
        </Route>

        {/* Billing Routes */}
        <Route
          path={ROUTES.BILLING.ROOT}
          element={
            <LaunchDarklyProvider>
              <AuthenticationGuard>
                <TrackUserProvider>
                  <OnScreenEditorsProvider>
                    <ErrorBoundary>
                      <Permissions
                        requiredFlags={[flagValues.billing]}
                        fallback={<NotFoundPage />}
                      >
                        {isRealNativePlatform() ? (
                          <WebOnlyPage />
                        ) : (
                          <ElementsWithTheme>
                            <SubscriptionGuard>
                              <Outlet />
                            </SubscriptionGuard>
                          </ElementsWithTheme>
                        )}
                      </Permissions>
                    </ErrorBoundary>
                  </OnScreenEditorsProvider>
                </TrackUserProvider>
              </AuthenticationGuard>
            </LaunchDarklyProvider>
          }
        >
          <Route path={ROUTES.BILLING.CHECKOUT} element={<CheckoutPage />} />
          <Route
            path={ROUTES.BILLING.CONFIRM_SETUP}
            element={<ConfirmSetupPage />}
          />
          <Route
            path={ROUTES.BILLING.ENTERPRISE_INQUIRY}
            element={<EnterpriseInquiryPage />}
          />
          {isSelfServeBilling && (
            <Route path={ROUTES.BILLING.PLANS} element={<PlansPage />} />
          )}
        </Route>

        <Route
          path="*"
          element={
            <LaunchDarklyProvider>
              <AuthenticationGuard>
                <EventStreamProvider>
                  <TrackUserProvider>
                    <PreloadDataProvider>
                      <SubscriptionGuard>
                        <ElementsProvider apiClient={orgAPI}>
                          <OnScreenEditorsProvider>
                            <OpenTabsProvider>
                              <TabPreloadProvider>
                                <ConditionalAppLayout />
                              </TabPreloadProvider>
                            </OpenTabsProvider>
                          </OnScreenEditorsProvider>
                        </ElementsProvider>
                      </SubscriptionGuard>
                    </PreloadDataProvider>
                  </TrackUserProvider>
                </EventStreamProvider>
              </AuthenticationGuard>
            </LaunchDarklyProvider>
          }
        />
      </Routes>
    </motion.div>
  );
}

export default React.memo(AppWithProviders);
